import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import LoginSideImg from '../../assets/SVGs/LogInSideImage';
import LoadingOverlay from '../insurance/LoadingOverlay';
import { initiateForgotPasswordApi } from '../../utils/apis';

/**
 * Single-step forgot-password flow (token+link):
 *   request -> PUT /auth/initiateForgotPassword (emails a reset link)
 * The user finishes on /reset-password?token=... (see ResetPassword.tsx).
 * This endpoint is enumeration-sensitive: the BE always returns a generic 200 ack, and the FE
 * keeps both the success and error copy generic — it never renders a server message here.
 */
const ForgotPassword: React.FC = () => {
  const { theme } = useTheme();

  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const inputClass =
    'w-full bg-white text-[#29406B] border-1 font-medium px-4 py-3 rounded-[12px] ring-1 ring-inset ring-[#DCE1E8]';
  const labelClass = 'font-semibold text-base text-[#29406B]';

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setMessage(null);
    try {
      await initiateForgotPasswordApi(username.trim());
      setMessage({
        type: 'success',
        text: "If an account exists for that username, we've emailed a reset link. Open it to finish resetting your password.",
      });
    } catch (error) {
      // Keep this generic and never render the server message — surfacing a backend string here
      // would reintroduce the account-enumeration vector the generic 200 ack is meant to close.
      setMessage({ type: 'error', text: "We couldn't start the reset. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      <div className="flex flex-1">
        <div className="flex flex-col justify-start md:justify-center w-full md:w-[55%] bg-[#F2F5F9] p-6 lg:p-12 sm:mt-0">
          <div className="sm:px-[32px] md:px-[64px]">
            <h2 className="mt-10 text-left text-2xl font-semibold leading-9 text-[#29406B] sm:mt-0">
              Reset your password
            </h2>
            <p className="mt-2 text-sm text-[#5b6b8c]">
              Enter your username and we'll email you a reset link.
            </p>
          </div>

          <div className="mt-6 sm:px-[32px] md:px-[64px]">
            {loading && <LoadingOverlay />}
            {message && (
              <div
                className={`p-4 rounded-md mb-4 ${
                  message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}
          </div>

          <div className="mt-4 sm:px-[32px] md:px-[64px] sm:mt-0">
            <form className="space-y-6" onSubmit={handleRequest}>
              <div>
                <label htmlFor="fp-username" className={labelClass}>Username</label>
                <div className="mt-2">
                  <input
                    id="fp-username"
                    type="text"
                    value={username}
                    required
                    onChange={(e) => { setUsername(e.target.value); setMessage(null); }}
                    className={inputClass}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`flex px-4 py-3 w-full justify-center rounded-md text-xl font-semibold text-white shadow-sm disabled:opacity-60 ${theme['button-bg']}`}
              >
                Send reset link
              </button>
            </form>

            <p className="mt-8 text-center text-base font-semibold">
              <Link to="/login" className={`text-${theme.color} underline cursor-pointer`}>Back to sign in</Link>
            </p>
          </div>
        </div>

        <div className="hidden md:flex md:w-[45%] items-center justify-center bg-[#242E49] h-screen">
          <LoginSideImg />
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
